/**
 * University hub slug registry — per-country university calendar pages with
 * multilingual URL support. Follows the slug-country-lens.ts pattern.
 *
 * E.g. /indonesia-university-calendars-2026 (en),
 *      /kalender-universitas-2026-indonesia (id)
 */

import type { CountryCode, University } from './types';
import { getCountries, getUniversities, getEventsForUniversity, getMinEventsThreshold, getCountryByCode } from './data-source';
import { YEAR, getCountryNameEntry, getPhraseEntry, getLanguagesForCountry, getLangLabel, getLangLabelEn } from './i18n-helpers';
import { buildLocalizedSlug } from './slug-word-order';

export interface UniversityHubSlugEntry {
  countryCode: CountryCode;
  countryName: string;
  countryFlag: string;
  slug: string;
  lang: string;
  universities: University[];
}

// ---------------------------------------------------------------------------
// Slug builder
// ---------------------------------------------------------------------------

function buildUniversityHubSlug(code: CountryCode, lang: string): string {
  const countryEntry = getCountryNameEntry(code, lang);
  const phrase = getPhraseEntry('university_calendars', lang);
  return buildLocalizedSlug({ subject: countryEntry.slug, phrase: phrase.slug, year: String(YEAR) }, lang);
}

// ---------------------------------------------------------------------------
// University data (shared with old university-hub.ts callers)
// ---------------------------------------------------------------------------

/** Cached per-country university list (language-independent) */
let _hubData: Map<CountryCode, { flag: string; universities: University[] }> | null = null;

function ensureHubData(): Map<CountryCode, { flag: string; universities: University[] }> {
  // Never memoize an empty result. getUniversities() may not be loaded yet on
  // the first call during the build; a plain `if (_hubData)` guard would freeze
  // that empty map forever, dropping EVERY university-hub page from the build
  // (they 404'd site-wide in prod while individual university pages — which load
  // data later — worked). Rebuild until the data lands. Mirrors the cross-
  // calendar empty-cache fix (almanak-keyh). (almanak-jl6y)
  if (_hubData && _hubData.size > 0) return _hubData;

  const universities = getUniversities();
  const countries = getCountries();
  const byCountry = new Map<CountryCode, University[]>();
  for (const uni of universities) {
    const list = byCountry.get(uni.country) ?? [];
    list.push(uni);
    byCountry.set(uni.country, list);
  }

  const threshold = getMinEventsThreshold('university');
  _hubData = new Map();
  for (const [code, unis] of byCountry) {
    const country = countries.find(c => c.code === code);
    if (!country) continue;
    const filtered = unis.filter(u => getEventsForUniversity(u.id).length >= threshold);
    if (filtered.length === 0) continue;
    _hubData.set(code, { flag: country.flag, universities: filtered });
  }
  return _hubData;
}

// ---------------------------------------------------------------------------
// Slug registry cache
// ---------------------------------------------------------------------------

let _uniHubSlugCache: Record<string, { code: CountryCode; lang: string }> | null = null;
let _uniHubReverseCache: Record<string, string> | null = null;

export function ensureUniversityHubBuilt() {
  if (_uniHubSlugCache) return;

  const hubData = ensureHubData();
  const cache: Record<string, { code: CountryCode; lang: string }> = {};
  const reverse: Record<string, string> = {};

  for (const [code] of hubData) {
    if (!getCountryNameEntry(code, 'en')) continue;

    const langs = getLanguagesForCountry(code);
    for (const lang of langs) {
      let slug = buildUniversityHubSlug(code, lang);
      if (cache[slug]) {
        slug = `${slug}-${lang}`;
      }
      cache[slug] = { code, lang };
      reverse[`${code}:${lang}`] = slug;
    }
  }

  if (Object.keys(cache).length === 0) return;

  _uniHubSlugCache = cache;
  _uniHubReverseCache = reverse;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getAllUniversityHubSlugs(): string[] {
  ensureUniversityHubBuilt();
  return Object.keys(_uniHubSlugCache ?? {});
}

export function getUniversityHubSlugEntry(slug: string): UniversityHubSlugEntry | null {
  ensureUniversityHubBuilt();
  const match = _uniHubSlugCache?.[slug];
  if (!match) return null;

  const hubData = ensureHubData();
  const data = hubData.get(match.code);
  if (!data) return null;

  const countryEntry = getCountryNameEntry(match.code, match.lang);

  return {
    countryCode: match.code,
    countryName: countryEntry.display,
    countryFlag: data.flag,
    slug,
    lang: match.lang,
    universities: data.universities,
  };
}

export function getAlternateUniversityHubLanguages(
  code: CountryCode,
): Array<{ lang: string; slug: string; label: string }> {
  ensureUniversityHubBuilt();
  const langs = getLanguagesForCountry(code);
  return langs
    .map(lang => ({
      lang,
      slug: _uniHubReverseCache?.[`${code}:${lang}`] ?? '',
      label: getLangLabel(lang),
      labelEn: getLangLabelEn(lang),
    }))
    .filter(x => x.slug);
}

export function getUniversityHubSlugForCountry(code: CountryCode, lang: string): string {
  ensureUniversityHubBuilt();
  return _uniHubReverseCache?.[`${code}:${lang}`]
    || _uniHubReverseCache?.[`${code}:en`]
    || '';
}

/** Internal: raw slug cache for cross-registry collision detection */
export function _getUniversityHubSlugCache(): Record<string, { code: CountryCode; lang: string }> {
  ensureUniversityHubBuilt();
  return _uniHubSlugCache ?? {};
}

/** Returns the set of country codes that have university hub pages */
export function getUniversityHubCountryCodes(): CountryCode[] {
  const hubData = ensureHubData();
  return [...hubData.keys()];
}
